// ABOUTME: Cart panel showing the active cart's line items with quantity edit and remove: a slide-out
// ABOUTME: drawer, or in the treeview layout a panel docked beside the page under the app bar.

import {
    Box,
    Button,
    CloseIcon,
    Divider,
    Drawer,
    IconButton,
    Stack,
    ThreeViewLayout,
    Typography,
} from '@ds/coolshop';
import { resolveImageUri } from '@src/_new/utils/resolveImageUri';
import { useGetNodeQuery } from '@src/components/hierarchies/Hierarchy.generated';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCalculatedFields } from '../../hooks/useCalculatedFields';
import { useCpqSettings } from '../../hooks/useCpqSettings';
import { readNodeName, resolveModelNameField } from '../../utils/modelNameContent';
import { resolveLineItemName } from '../../utils/resolveLineItemName';
import { CartCatalogDialog } from './CartCatalogDialog';
import { useCartContext } from './CartContext';
import { CartDockContext } from './CartDockContext';
import { CartLineItemRow } from './CartLineItemRow';
import { ConvertCartDialog } from './ConvertCartDialog';

type LineItemContent = {
    contentTypeGroup: string;
    contentType: string;
    value: Array<{ value: string[] }>;
};

type RawNode = {
    id: string;
    code?: string | null;
    contents?: Array<{ contentType: string; value?: Array<{ value: string[] }> | null }> | null;
};

function collectNames(nodes: RawNode[], nameField: string): Record<string, string> {
    const map: Record<string, string> = {};
    nodes.forEach(node => {
        const name = readNodeName(node.contents, nameField);
        if (name) map[node.id] = name;
    });
    return map;
}

// The image of each node, read by content type like the name, as a URL the page can load.
function collectImages(nodes: RawNode[], imageField: string): Record<string, string> {
    const map: Record<string, string> = {};
    nodes.forEach(node => {
        const image = readNodeName(node.contents, imageField).trim();
        if (image) map[node.id] = resolveImageUri(image);
    });
    return map;
}

function readContent(item: Record<string, unknown>, group: string, field: string): string {
    const contents = (item.contents ?? []) as LineItemContent[];
    const content = contents.find(c => c.contentTypeGroup === group && c.contentType === field);
    return content?.value?.[0]?.value?.[0] ?? '';
}

// Width of the cart drawer, as in the cart visuals.
const CART_DRAWER_WIDTH = '520px';

type CartDrawerProps = {
    /** The page the cart opens over, or beside when docked. */
    children: ReactNode;
};

export function CartDrawer({ children }: CartDrawerProps) {
    const { formatMessage, formatNumber } = useIntl();
    const cart = useCartContext();
    const settings = useContext(SettingsContext);
    // In the treeview layout the cart docks beside the page, as in the visuals; elsewhere it
    // stays the drawer it always was.
    const docked = settings.prototype?.modelSelectionLayout === 'treeview';
    const prototypeHierarchyId = settings.prototype?.hierarchies?.prototype;
    const modelHierarchyId = settings.prototype?.hierarchies?.model;
    // Same setting the offer table and the catalog use for the human-readable model name.
    const nameField = resolveModelNameField(settings.prototype?.contentTypes?.modelName);
    // The catalog's image field, `image` when the tenant configured none.
    const imageField = settings.prototype?.contentTypes?.modelImage?.contentType || 'image';

    const [catalogOpen, setCatalogOpen] = useState(false);
    const [convertOpen, setConvertOpen] = useState(false);

    const lineItems = cart?.cart?.lineItems ?? [];

    const prototypeIds = useMemo(() => {
        const ids = new Set<string>();
        lineItems.forEach(item => {
            const id = readContent(item, 'line_item_info', 'prototype');
            if (id) ids.add(id);
        });
        return Array.from(ids);
    }, [lineItems]);

    const { data: prototypeNodesData } = useGetNodeQuery({
        variables: {
            hierarchiesId: prototypeHierarchyId,
            nodeFilter: { ids: prototypeIds },
        },
        fetchPolicy: 'cache-first',
        skip: prototypeIds.length === 0 || !prototypeHierarchyId,
    });

    const prototypeIdToCode = useMemo(() => {
        const map: Record<string, string> = {};
        const nodes = (prototypeNodesData?.hierarchies?.[0]?.nodes ?? []) as Array<{
            id: string;
            code?: string | null;
        }>;
        nodes.forEach(node => {
            if (node.code) map[node.id] = node.code;
        });
        return map;
    }, [prototypeNodesData]);

    const prototypeIdToName = useMemo(
        () =>
            collectNames(
                (prototypeNodesData?.hierarchies?.[0]?.nodes ?? []) as RawNode[],
                nameField,
            ),
        [prototypeNodesData, nameField],
    );

    // Non-configurable items reference the product directly via the `model` link; resolve
    // their names from the model nodes.
    const modelIds = useMemo(() => {
        const ids = new Set<string>();
        lineItems.forEach(item => {
            const id = readContent(item, 'line_item_info', 'model');
            if (id) ids.add(id);
        });
        return Array.from(ids);
    }, [lineItems]);

    const { data: modelNodesData } = useGetNodeQuery({
        variables: {
            hierarchiesId: modelHierarchyId,
            nodeFilter: { ids: modelIds },
        },
        fetchPolicy: 'cache-first',
        skip: modelIds.length === 0 || !modelHierarchyId,
    });

    const modelIdToCode = useMemo(() => {
        const map: Record<string, string> = {};
        const nodes = (modelNodesData?.hierarchies?.[0]?.nodes ?? []) as Array<{
            id: string;
            code?: string | null;
        }>;
        nodes.forEach(node => {
            if (node.code) map[node.id] = node.code;
        });
        return map;
    }, [modelNodesData]);

    const modelIdToName = useMemo(
        () => collectNames((modelNodesData?.hierarchies?.[0]?.nodes ?? []) as RawNode[], nameField),
        [modelNodesData, nameField],
    );

    const nodeIdToImage = useMemo(
        () => ({
            ...collectImages(
                (prototypeNodesData?.hierarchies?.[0]?.nodes ?? []) as RawNode[],
                imageField,
            ),
            ...collectImages(
                (modelNodesData?.hierarchies?.[0]?.nodes ?? []) as RawNode[],
                imageField,
            ),
        }),
        [prototypeNodesData, modelNodesData, imageField],
    );

    // The cart is an offer, so its subtotal is the offer's: the OFFER `offer_totals.subtotal`
    // calculated field, evaluated by the server as on the offer page (optional lines skipped, line
    // discounts applied). The cart's stored totals are only written when it is converted.
    const offersHierarchyId = useCpqSettings()?.hierarchies?.offers;
    const cartOffer = cart?.cart?.offer;
    const { calculatedFields, evaluateCalculatedFields } = useCalculatedFields();
    useEffect(() => {
        const cartOfferId = cartOffer?.id as string | undefined;
        if (!offersHierarchyId || !cartOfferId) return;
        evaluateCalculatedFields(
            offersHierarchyId,
            cartOfferId,
            (cartOffer?.contents ?? []) as Parameters<typeof evaluateCalculatedFields>[2],
        );
        // Re-evaluated whenever the cart is reloaded, i.e. after every line item change.
    }, [offersHierarchyId, cartOffer, lineItems, evaluateCalculatedFields]);
    const subtotalField = calculatedFields.find(
        field => field.contentTypeGroup === 'offer_totals' && field.contentType === 'subtotal',
    );
    const subtotal = subtotalField ? Object.values(subtotalField.value)[0] : undefined;

    if (!cart || !cart.enabled) {
        return children;
    }

    // The cart is an offer: its currency prices every line.
    const currency = cart.cart
        ? readContent(cart.cart.offer, 'offer_assignment', 'currency') || undefined
        : undefined;
    // A currency that is not a valid ISO 4217 code makes Intl throw: the price is then shown as a
    // plain number rather than breaking the whole panel.
    const formatPrice = (value: number) => {
        if (!currency) return formatNumber(value);
        try {
            return formatNumber(value, { style: 'currency', currency });
        } catch {
            return formatNumber(value);
        }
    };
    const lineTotals = lineItems.map(item =>
        parseFloat(readContent(item, 'line_item_pricing', 'line_total')),
    );

    const panel = (
        <Box width="100%" maxWidth="100vw" display="flex" flexDirection="column" height="100%">
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                px={3}
                pt={2}
            >
                <Stack direction="row" alignItems="center" gap={1}>
                    <IconButton
                        aria-label={formatMessage({
                            id: 'common.close',
                            defaultMessage: 'Close',
                        })}
                        onClick={cart.closeDrawer}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography variant="h6">
                        {formatMessage({ id: 'cpq.cart.title', defaultMessage: 'Cart' })}
                    </Typography>
                </Stack>
                {subtotal !== undefined && subtotal !== '' && lineItems.length > 0 && (
                    <Stack alignItems="flex-end">
                        <Typography variant="caption" color="primary">
                            {formatMessage({
                                id: 'cpq.cart.subtotal',
                                defaultMessage: 'Subtotal',
                            })}
                        </Typography>
                        <Typography variant="subtitle2" data-testid="cart-total">
                            {formatPrice(Number(subtotal))}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            <Box flexGrow={1} overflow="auto" px={3} pt={1}>
                {lineItems.length === 0 ? (
                    <Box py={2}>
                        <Typography color="textSecondary">
                            {formatMessage({
                                id: 'cpq.cart.empty',
                                defaultMessage: 'Your cart is empty.',
                            })}
                        </Typography>
                    </Box>
                ) : (
                    <Stack borderTop="1px solid" borderColor="divider">
                        {lineItems.map((item, index) => {
                            const lineItemId = item.id as string;
                            const prototypeId = readContent(item, 'line_item_info', 'prototype');
                            const modelId = readContent(item, 'line_item_info', 'model');
                            const name = resolveLineItemName({
                                productName: readContent(item, 'line_item_info', 'product_name'),
                                prototypeName: prototypeIdToName[prototypeId],
                                modelName: modelIdToName[modelId],
                                prototypeCode: prototypeIdToCode[prototypeId],
                                modelCode: modelIdToCode[modelId],
                                fallback: (item.code as string) || lineItemId,
                            });
                            const quantity =
                                parseInt(readContent(item, 'line_item_info', 'quantity'), 10) || 1;
                            const lineTotal = lineTotals[index];
                            return (
                                <CartLineItemRow
                                    key={lineItemId}
                                    name={name}
                                    code={prototypeIdToCode[prototypeId] ?? modelIdToCode[modelId]}
                                    imageUrl={nodeIdToImage[prototypeId] ?? nodeIdToImage[modelId]}
                                    totalPrice={
                                        Number.isFinite(lineTotal)
                                            ? formatPrice(lineTotal)
                                            : undefined
                                    }
                                    quantity={quantity}
                                    onQuantityChange={next =>
                                        cart.updateItem(lineItemId, { quantity: next })
                                    }
                                    onRemove={() => cart.removeItem(lineItemId)}
                                    disabled={cart.loading}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Box>

            <Box px={3}>
                <Divider />
            </Box>
            <Stack direction="row" justifyContent="flex-end" gap={1} px={3} py={2}>
                {/* White with dark text, as the secondary actions elsewhere in PIM. */}
                <Button
                    variant="contained"
                    invertedColor
                    onClick={() => cart.discard()}
                    disabled={!cart.cart}
                >
                    {formatMessage({
                        id: 'cpq.cart.discard',
                        defaultMessage: 'Discard cart',
                    })}
                </Button>
                <Button variant="contained" onClick={() => setCatalogOpen(true)}>
                    {formatMessage({
                        id: 'cpq.cart.addProducts',
                        defaultMessage: 'Add products',
                    })}
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setConvertOpen(true)}
                    disabled={lineItems.length === 0}
                >
                    {formatMessage({
                        id: 'cpq.cart.convertToOffer',
                        defaultMessage: 'Convert to offer',
                    })}
                </Button>
            </Stack>
        </Box>
    );

    const dialogs = (
        <>
            <CartCatalogDialog open={catalogOpen} onClose={() => setCatalogOpen(false)} />
            <ConvertCartDialog open={convertOpen} onClose={() => setConvertOpen(false)} />
        </>
    );

    if (docked) {
        // The layout's right panel sits under the app bar and narrows the page instead of
        // covering it, so the page scrolls on its own and the panel stays put.
        return (
            <>
                <ThreeViewLayout
                    center={
                        <Box height="100%" overflow="auto">
                            {/* The page's sidebars make room for the open cart. */}
                            <CartDockContext.Provider value={cart.isDrawerOpen}>
                                {children}
                            </CartDockContext.Provider>
                        </Box>
                    }
                    right={cart.isDrawerOpen ? panel : undefined}
                    overrideRightWidth={CART_DRAWER_WIDTH}
                />
                {dialogs}
            </>
        );
    }

    return (
        <>
            {children}
            {/* The DS drawer sizes its own paper: the width goes to it, the content fills it. */}
            <Drawer
                anchor="right"
                width={CART_DRAWER_WIDTH}
                open={cart.isDrawerOpen}
                onClose={cart.closeDrawer}
            >
                {panel}
            </Drawer>
            {dialogs}
        </>
    );
}
